import { useMemo } from "react";
import { DonutChart, StackedBars, TrendLine } from "@ncp/ui";
import { screenText } from "./text";
import { pick, type Locale, type ReportResult } from "./types";

export function formatCell(v: unknown, locale: Locale): string {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return screenText(locale)(v ? "yes" : "no");
  if (typeof v === "number") return new Intl.NumberFormat(locale === "ar" ? "ar-AE" : "en-GB", { maximumFractionDigits: 2 }).format(v);
  return String(v);
}

/** A report result as a chart (bar, line, pie or headline figures) above its table. */
export function ReportView({ result, locale, height = 260, showTable = true, maxRows = 200 }: { result: ReportResult; locale: Locale; height?: number; showTable?: boolean; maxRows?: number }) {
  const t = screenText(locale);
  const numeric = useMemo(() => result.columns.map((c, i) => ({ c, i })).filter(({ c }) => c.type === "number" || c.type === "money"), [result.columns]);
  const categories = useMemo(() => result.rows.map((r) => formatCell(r[0], locale)), [result.rows, locale]);
  const series = useMemo(() => numeric.map(({ c, i }) => ({ name: pick(locale, c.label, c.labelAr), data: result.rows.map((r) => Number(r[i] ?? 0)) })), [numeric, result.rows, locale]);

  const chart = (() => {
    if (result.rows.length === 0 || numeric.length === 0) return null;
    switch (result.chart) {
      case "bar": return <StackedBars categories={categories} series={series} height={height} />;
      case "line": return <TrendLine categories={categories} series={series} height={height} area={false} />;
      case "pie": return <DonutChart data={categories.map((name, j) => ({ name, value: series[0]?.data[j] ?? 0 }))} height={height} />;
      case "kpi": return (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {numeric.map(({ c, i }) => (
            <div key={c.key} className="rounded-2xl border border-sand-200 bg-white/80 p-4">
              <div className="text-xs text-muted-foreground">{pick(locale, c.label, c.labelAr)}</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">{formatCell(result.rows[0]?.[i], locale)}</div>
            </div>
          ))}
        </div>
      );
      default: return null;
    }
  })();

  return (
    <div className="space-y-3" data-testid={`report-${result.key}`}>
      {chart}
      {showTable && result.chart !== "kpi" ? (
        result.rows.length === 0 ? <p className="text-sm text-muted-foreground">{t("noRows")}</p> : (
          <div className="overflow-x-auto rounded-xl border border-sand-200">
            <table className="w-full text-sm">
              <thead className="bg-sand-50 text-xs text-muted-foreground">
                <tr>{result.columns.map((c) => <th key={c.key} className={`px-3 py-2 font-medium ${c.type === "number" || c.type === "money" ? "text-end" : "text-start"}`}>{pick(locale, c.label, c.labelAr)}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-sand-100">
                {result.rows.slice(0, maxRows).map((r, j) => (
                  <tr key={j}>{r.map((v, i) => <td key={i} className={`px-3 py-1.5 ${typeof v === "number" ? "text-end tabular-nums" : ""}`}>{formatCell(v, locale)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
      <p className="text-xs text-muted-foreground">{t("sourceRows", { n: result.sourceRows })} · {t("generated", { at: new Intl.DateTimeFormat(locale === "ar" ? "ar-AE" : "en-GB", { dateStyle: "medium", timeStyle: "short" }).format(new Date(result.generatedAt)) })}</p>
    </div>
  );
}
